import { useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import {
  changeSource,
  type ControlledSource,
  type SourceDescriptor,
  type SourceSettings,
} from "@/api/filth-source-control-api"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

import {
  sourceControlKey as key,
  sourceControlError,
  sourceTargetLabel,
  validSourceSettings,
} from "./filth-source-control-utils"
import { FilthSourceSettingsFields } from "./filth-source-settings-fields"

// Null include_extensions / max_file_bytes mean "inherit". The form shows a
// placeholder so it can render, but an interval-only save must omit those keys.
// Sending the placeholder would materialize it; the API rejects an explicit null.
function settingsToSave(
  settings: SourceSettings,
  stored: ControlledSource["settings"],
  edited: { extensions: boolean; size: boolean }
) {
  const body: {
    include_extensions?: string[]
    max_file_bytes?: number
    interval_seconds: number | null
  } = { interval_seconds: settings.interval_seconds }
  if (stored.include_extensions !== null || edited.extensions) {
    body.include_extensions = settings.include_extensions
  }
  if (stored.max_file_bytes !== null || edited.size) {
    body.max_file_bytes = settings.max_file_bytes
  }
  return body
}

export function FilthEditSourceDialog({
  backendId,
  source,
  descriptor,
  onClose,
  onSaved,
}: {
  backendId: string
  source: ControlledSource
  descriptor: SourceDescriptor
  onClose: () => void
  onSaved: (effect?: string) => void
}) {
  const { t } = useTranslation()
  const stored = source.settings
  const [extensionsEdited, setExtensionsEdited] = useState(false)
  const [sizeEdited, setSizeEdited] = useState(false)
  const [settings, setSettings] = useState({
    ...stored,
    include_extensions: stored.include_extensions ?? ["pdf", "docx"],
    max_file_bytes: stored.max_file_bytes ?? descriptor.ceilings.max_file_bytes,
  })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const action = useRef<{ fingerprint: string; key: string } | null>(null)
  const sentSettings = settingsToSave(settings, stored, {
    extensions: extensionsEdited,
    size: sizeEdited,
  })
  const resync =
    (sentSettings.include_extensions !== undefined &&
      JSON.stringify(sentSettings.include_extensions) !==
        JSON.stringify(stored.include_extensions)) ||
    (sentSettings.max_file_bytes !== undefined &&
      sentSettings.max_file_bytes !== stored.max_file_bytes)
  function onSettingsChange(next: SourceSettings) {
    if (JSON.stringify(next.include_extensions) !== JSON.stringify(settings.include_extensions)) {
      setExtensionsEdited(true)
    }
    if (next.max_file_bytes !== settings.max_file_bytes) setSizeEdited(true)
    setSettings(next)
  }
  async function save() {
    const body = { settings: sentSettings }
    const fingerprint = JSON.stringify(body)
    if (action.current?.fingerprint !== fingerprint)
      action.current = { fingerprint, key: crypto.randomUUID() }
    setBusy(true)
    setError(null)
    try {
      const updated = await changeSource(backendId, "update", {
        body,
        key: action.current!.key,
        source,
      })
      onSaved(updated.effect)
      onClose()
    } catch (e) {
      setError(sourceControlError(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose()
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t(`${key}.edit`)}</DialogTitle>
          <DialogDescription>{sourceTargetLabel(source.target)}</DialogDescription>
        </DialogHeader>
        <FilthSourceSettingsFields
          editing
          descriptor={descriptor}
          kind={source.kind}
          value={settings}
          onChange={onSettingsChange}
        />
        {resync && (
          <p className="text-sm" role="status">
            {t(`${key}.resyncRequired`)}
          </p>
        )}
        {error && <p role="alert">{t(`${key}.errors.${error}`)}</p>}
        <div className="flex justify-end gap-2">
          <Button disabled={busy} variant="outline" onClick={onClose}>
            {t(`${key}.cancel`)}
          </Button>
          <Button
            disabled={busy || !validSourceSettings(settings, descriptor.ceilings)}
            onClick={() => void save()}
          >
            {t(`${key}.save`)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
